/*
* @miii/vue-lettering
* Version: 1.1.3 (2020-04-27)
* Author: Jacob Andersson
* License: MIT
*/
import deepmerge from 'deepmerge';

var version = "1.1.3";

// Default plugin options
var defaults = {
    tagName: 'span',
    splits: [''],
    char: function (text) { return text.replace(' ', '&nbsp;'); },
    className: null,
    classNameInjection: {
        group: true,
        level: true,
        index: true
    }
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var setDefaultOption = function (key, value) {
    if (key === 'classNameInjection')
        // Merge with existing config
        defaults.classNameInjection = Object.assign({}, defaults.classNameInjection, value);
    else
        // All other properties
        defaults[key] = value;
};
var classNames = {
    group: 'vl__g',
    level: function (n) { return "vl--lvl-" + n; },
    index: function (n) { return "vl--i-" + n; }
};
/**
 * Directive bind callback
 * @param el HTML element
 * @param binding Vue directive binding
 * @param vnode Vue VNode
 */
var bind = function (el, binding, vnode) {
    var _a, _b, _c, _d, _e;
    var text = ((_c = (_b = (_a = vnode === null || vnode === void 0 ? void 0 : vnode.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.text) === null || _c === void 0 ? void 0 : _c.trim()) || ((_e = (_d = vnode === null || vnode === void 0 ? void 0 : vnode.elm) === null || _d === void 0 ? void 0 : _d.textContent) === null || _e === void 0 ? void 0 : _e.trim()) || '';
    // Create options object
    var bindingValue = binding.value;
    if (bindingValue && (typeof bindingValue !== 'object' || Array.isArray(bindingValue)))
        bindingValue = { splits: bindingValue };
    var options = deepmerge(defaults, bindingValue || {}, {
        arrayMerge: function (dest, source) { return source; }
    });
    options.tagName = Object.keys(binding.modifiers)[0] || options.tagName;
    // Create array/function if strings are provided
    var splits = !Array.isArray(options.splits) ? [options.splits] : options.splits;
    var setClassName = typeof options.className !== 'function' ? (function () { return options.className; }) : options.className;
    // Create and append character subelements
    el.innerHTML = '';
    if (!el.getAttribute('aria-label'))
        el.setAttribute('aria-label', text);
    /**
     * Run recursive split functions
     * @param el Parent element
     * @param groups List of character groups
     * @param level Current recursion depth
     */
    var runSplit = function (el, groups, level) {
        if (level === void 0) { level = 1; }
        // Check if all leafs are found
        if (level > splits.length) {
            if (typeof options.char !== 'function')
                console.error('Error', options);
            // Add character
            el.innerHTML = options.char(groups[0]);
            // Return leaf element
            return;
        }
        // Create new element for each character group
        groups.forEach(function (group, index) {
            var subEl = document.createElement(options.tagName);
            el.append(subEl);
            if (level === 1)
                subEl.setAttribute('aria-hidden', 'true');
            // Set custom class name if provided
            var className = setClassName(index + 1, level, group);
            if (className)
                subEl.classList.add(className);
            // Inject helper class names
            if (options.classNameInjection) {
                if (options.classNameInjection.group)
                    subEl.classList.add(classNames.group);
                if (options.classNameInjection.level && splits.length > 1)
                    subEl.classList.add(classNames.level(level));
                if (options.classNameInjection.index)
                    subEl.classList.add(classNames.index(index + 1));
            }
            // Run beforeAppend hook
            if (options.beforeAppend)
                options.beforeAppend(subEl, index + 1, level);
            // Split another level
            runSplit(subEl, group.split(splits[level]), level + 1);
        });
    };
    // Initiate split
    runSplit(el, text.split(splits[0]));
    // Remove loading selector
    el.removeAttribute('data-lettering');
};
var directive = {
    bind: bind,
    componentUpdated: bind
};

var install = function (Vue, options) {
    // Merge options
    if (options)
        Object.keys(options)
            .forEach(function (key) { return setDefaultOption(key, options[key]); });
    Vue.directive('lettering', directive);
};
var plugin = {
    install: install,
    version: version
};
if (typeof window !== 'undefined' && window.Vue)
    window.Vue.use(plugin);

export default plugin;
